import { useState, useEffect } from 'react';
import { 
    getMemberCountsFromSheets, 
    getTotalMembersFromSheets, 
    getTotalCountriesFromSheets 
} from '../utils/googleSheetsData';

// Fallback to static data if Google Sheets fails
import { 
    getMemberCounts as getStaticMemberCounts, 
    getTotalMembers as getStaticTotalMembers, 
    getTotalCountries as getStaticTotalCountries 
} from '../utils/memberData';

/**
 * Custom hook to fetch member data from Google Sheets
 * Falls back to static data if fetch fails
 * 
 * @param {boolean} useGoogleSheets - Whether to use Google Sheets (set to false to use static data)
 * @returns {{ memberCounts, totalMembers, totalCountries, loading, error }}
 */
export function useMemberData(useGoogleSheets = false) {
    const [memberCounts, setMemberCounts] = useState({});
    const [totalMembers, setTotalMembers] = useState(0);
    const [totalCountries, setTotalCountries] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError(null);

            try {
                if (useGoogleSheets) {
                    // Fetch from Google Sheets
                    const [counts, total, countries] = await Promise.all([
                        getMemberCountsFromSheets(),
                        getTotalMembersFromSheets(),
                        getTotalCountriesFromSheets()
                    ]);
                    
                    setMemberCounts(counts);
                    setTotalMembers(total);
                    setTotalCountries(countries);
                } else {
                    // Use static data
                    setMemberCounts(getStaticMemberCounts());
                    setTotalMembers(getStaticTotalMembers());
                    setTotalCountries(getStaticTotalCountries());
                }
            } catch (err) {
                console.error('Error loading member data:', err);
                setError(err);
                
                // Fallback to static data on error
                setMemberCounts(getStaticMemberCounts());
                setTotalMembers(getStaticTotalMembers());
                setTotalCountries(getStaticTotalCountries());
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [useGoogleSheets]);

    return { memberCounts, totalMembers, totalCountries, loading, error };
}

export default useMemberData;
